import { useState, useRef, useEffect, type ChangeEvent, type DragEvent } from 'react'
import { UploadCloud, X, FileText } from 'lucide-react'
import { Button } from './ui/button'
import { cn } from '@/lib/utils'

interface FileUploaderProps {
  onFileSelect: (file: File | null) => void
  /** When provided, the component is controlled — it displays this file instead of managing its own state */
  value?: File | null
  /** Called on any drop event, valid or not — useful for parent to reset drag-over state */
  onDrop?: () => void
  /** Disables the remove (X) button while scanning is in progress */
  isScanning?: boolean
}

// Convert a HEIC/HEIF file to JPEG using heic-to (WASM-based, works in all browsers).
// Loaded lazily so the WASM bundle is only fetched when a HEIC file is actually selected.
async function convertHeicToJpeg(file: File): Promise<File> {
  const { heicTo } = await import('heic-to')
  const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.85 })
  const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
  return new File([blob], jpegName, { type: 'image/jpeg' })
}

export function FileUploader({ onFileSelect, value, onDrop, isScanning }: FileUploaderProps) {
  const isControlled = value !== undefined

  const [internalFile, setInternalFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [typeError, setTypeError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const file = isControlled ? value : internalFile

  // Rebuild preview URL whenever the active file changes
  useEffect(() => {
    if (preview) URL.revokeObjectURL(preview)
    if (file) {
      setPreview(URL.createObjectURL(file))
    } else {
      setPreview(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setIsDragging(true) }
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setIsDragging(false) }
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    onDrop?.()
    const f = e.dataTransfer.files?.[0]
    if (f) acceptFile(f)
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) acceptFile(f)
  }

  const acceptFile = (f: File) => {
    const isHeic = f.type === 'image/heic' || f.type === 'image/heif'
      || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif')

    if (isHeic) {
      setTypeError('')
      convertHeicToJpeg(f).then((jpeg) => {
        if (!isControlled) setInternalFile(jpeg)
        onFileSelect(jpeg)
      }).catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'HEIC conversion failed'
        setTypeError(msg)
      })
      return
    }

    if (f.type.startsWith('image/') || f.type === 'application/pdf') {
      setTypeError('')
      if (!isControlled) setInternalFile(f)
      onFileSelect(f)
    } else {
      setTypeError('Unsupported file type. Please upload an image or PDF.')
    }
  }

  const removeFile = () => {
    if (!isControlled) setInternalFile(null)
    onFileSelect(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="w-full">
      <input
        type="file"
        ref={inputRef}
        className="hidden"
        accept="image/*,application/pdf"
        onChange={handleFileChange}
      />
      {!file ? (
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-colors",
            isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <div className="bg-primary/10 p-3 rounded-full mb-3">
            <UploadCloud className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm font-medium text-center mb-1">
            Click to upload or drag and drop
          </p>
          <p className="text-xs text-muted-foreground text-center">
            Receipts (PDF, PNG, JPG, HEIC)
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="relative rounded-lg overflow-hidden border max-h-64 flex justify-center bg-muted/30 min-h-[150px] items-center">
            {preview && file.type.startsWith('image/') ? (
              <img src={preview} alt="Receipt preview" className="object-contain max-h-64 w-full" />
            ) : preview && file.type === 'application/pdf' ? (
              <object data={preview} type="application/pdf" width="100%" className="h-full">
                <div className="flex flex-col items-center text-muted-foreground p-4 text-center">
                  <FileText className="h-10 w-10 mb-2 opacity-50" />
                  <p className="text-sm font-medium">Cannot preview PDF</p>
                  <p className="text-xs">Your browser may not support PDF previews.</p>
                </div>
              </object>
            ) : (
              <div className="flex flex-col items-center text-muted-foreground p-4">
                <FileText className="h-10 w-10 mb-2 opacity-50" />
                <p className="text-sm">No preview available</p>
              </div>
            )}
          </div>
          <div className="border rounded-lg p-3 flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="bg-primary/10 p-2 rounded-md flex-shrink-0">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            </div>
            <Button type="button" variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={removeFile} disabled={isScanning}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      {typeError && <p className="text-sm text-destructive mt-1.5">{typeError}</p>}
    </div>
  )
}
